import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Cloud, Building2, Globe2 } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";

const VERTICALS = [
  {
    num: "01",
    icon: Cloud,
    title: "SaaS & Cloud Systems",
    focus: "Building, hosting, and scaling specialized cloud software.",
    highlight:
      "Engineering modern enterprise solutions—such as food service cost management and recipe intelligence platforms—helping businesses optimize operational margins globally.",
    img: "https://images.unsplash.com/photo-1644088379091-d574269d422f?q=80&w=1200&auto=format&fit=crop",
    alt: "Abstract cloud data network",
    testId: "vertical-saas",
  },
  {
    num: "02",
    icon: Building2,
    title: "Commercial Real Estate & Asset Leasing",
    focus: "Redeveloping and leasing high-utility commercial properties.",
    highlight:
      "Developing customized utility infrastructure, commercial spaces, and multi-use facilities designed to host premium retail, hospitality, and corporate lessees.",
    img: "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?q=80&w=1200&auto=format&fit=crop",
    alt: "Low angle view of a commercial building",
    testId: "vertical-real-estate",
  },
  {
    num: "03",
    icon: Globe2,
    title: "Retail Trading & Global Commerce",
    focus: "Merchant trading, distribution, and international trade.",
    highlight:
      "Operating fully compliant import-export channels and retail trading operations to connect products and service networks across international borders.",
    img: "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?q=80&w=1200&auto=format&fit=crop",
    alt: "Aerial view of a cargo ship at sea",
    testId: "vertical-trading",
  },
];

const ParallaxImage = ({ src, alt, testId }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div
      ref={ref}
      className="group relative overflow-hidden rounded-[1.75rem] border border-navy/10 aspect-[4/5] max-h-[560px] w-full shadow-[0_30px_60px_rgba(28,43,96,0.15)]"
    >
      <motion.img
        src={src}
        alt={alt}
        data-testid={`${testId}-image`}
        style={{ y }}
        className="w-full h-[118%] object-cover scale-110 transition-[transform,filter] duration-700 group-hover:scale-115"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent opacity-70" />
    </div>
  );
};

const VerticalRow = ({ v, flip }) => (
  <FadeUp className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
    <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
      <ParallaxImage src={v.img} alt={v.alt} testId={v.testId} />
    </div>
    <div className={`lg:col-span-7 relative ${flip ? "lg:order-1" : ""}`}>
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-14 lg:-top-20 -left-2 font-display font-bold text-[6rem] lg:text-[9rem] leading-none stroke-number"
      >
        {v.num}
      </span>
      <div className="relative">
        <div className="flex items-center gap-4">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-gradient text-white shadow-[0_10px_24px_rgba(130,24,103,0.35)]">
            <v.icon className="w-5 h-5" />
          </span>
          <span className="uppercase tracking-[0.26em] text-xs font-bold text-peri">
            Vertical {v.num}
          </span>
        </div>
        <h3
          data-testid={`${v.testId}-title`}
          className="mt-6 font-display font-semibold text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.15] tracking-tight text-navy"
        >
          {v.title}
        </h3>
        <p data-testid={`${v.testId}-focus`} className="mt-5 text-base sm:text-lg font-semibold text-navy/85">
          {v.focus}
        </p>
        <p data-testid={`${v.testId}-highlight`} className="mt-3 text-base leading-relaxed text-navy/60 max-w-xl">
          {v.highlight}
        </p>
      </div>
    </div>
  </FadeUp>
);

const Verticals = () => (
  <section
    id="verticals"
    data-testid="verticals-section"
    className="relative bg-white py-28 lg:py-40 overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-20 lg:mb-28">
        <div className="lg:col-span-8">
          <FadeUp>
            <p className="uppercase tracking-[0.28em] text-xs sm:text-sm font-bold text-peri flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-peri" />
              Core Verticals
            </p>
          </FadeUp>
          <h2
            data-testid="verticals-heading"
            className="mt-8 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-navy"
          >
            <MaskedLine delay={0.1}>Three disciplines.</MaskedLine>
            <MaskedLine delay={0.22}>
              <span className="text-grad">One enterprise.</span>
            </MaskedLine>
          </h2>
        </div>
        <FadeUp delay={0.3} className="lg:col-span-4">
          <p className="text-sm sm:text-base leading-relaxed text-navy/60">
            A portfolio engineered for durability — cloud platforms, commercial assets, and
            trade networks that compound value across cycles.
          </p>
        </FadeUp>
      </div>

      <div className="flex flex-col gap-24 lg:gap-36">
        {VERTICALS.map((v, i) => (
          <VerticalRow key={v.num} v={v} flip={i % 2 === 1} />
        ))}
      </div>
    </div>
  </section>
);

export default Verticals;
